"use client";

import { useEffect, useMemo, useState } from "react";
import { strToU8, zipSync } from "fflate";
import {
  BUILDS_API_PATH,
  READINESS_API_PATH,
  bearerHeaders,
  isBuildJob,
  isOneTimeCredentials,
  isProvisionerReadiness,
  isValidAdminEmail,
  isValidDevLinksDomain,
  isValidSiteSlug,
  normalizeSiteSlug,
  type BuildJob,
  type OneTimeCredentials,
  type ProvisionerReadiness,
} from "./provisioning-contract";

type PageChoice = {
  id: string;
  name: string;
  description: string;
  sections: string[];
  required?: boolean;
};

type StylePreset = {
  id: string;
  name: string;
  description: string;
  colors: [string, string, string, string];
  heading: string;
  body: string;
};

type PreparedPackage = {
  file: File;
  fingerprint: string;
  sha256: string;
};

const pageChoices: PageChoice[] = [
  {
    id: "home",
    name: "Home",
    description: "Primary story, services overview and conversion path.",
    sections: ["Hero", "Value proposition", "Services", "Proof", "Primary CTA"],
    required: true,
  },
  {
    id: "about",
    name: "About",
    description: "Company story, point of view, values and credibility.",
    sections: ["Intro hero", "Story", "Values", "Proof", "CTA"],
  },
  {
    id: "services",
    name: "Services",
    description: "A clear overview of everything the business offers.",
    sections: ["Services hero", "Service grid", "Process", "FAQ", "CTA"],
  },
  {
    id: "service-detail",
    name: "Service detail",
    description: "Reusable detail-page system for each core service.",
    sections: ["Service hero", "Benefits", "How it works", "Proof", "FAQ", "CTA"],
  },
  {
    id: "team",
    name: "Team",
    description: "People, roles, biographies and human credibility.",
    sections: ["Team intro", "People grid", "Credentials", "Culture", "CTA"],
  },
  {
    id: "process",
    name: "How it works",
    description: "Make the buying or onboarding process easy to understand.",
    sections: ["Process hero", "Steps", "Expectations", "FAQ", "CTA"],
  },
  {
    id: "case-studies",
    name: "Case studies",
    description: "Outcomes, examples, testimonials and supporting proof.",
    sections: ["Results hero", "Featured story", "Results grid", "Testimonials", "CTA"],
  },
  {
    id: "faq",
    name: "FAQ",
    description: "A focused objection-handling and answer page.",
    sections: ["FAQ intro", "Question groups", "Still need help", "CTA"],
  },
  {
    id: "blog",
    name: "Insights",
    description: "Article archive and reusable single-post templates.",
    sections: ["Insights hero", "Featured post", "Article grid", "Newsletter CTA"],
  },
  {
    id: "contact",
    name: "Contact",
    description: "Contact routes, form, location and next-step expectations.",
    sections: ["Contact hero", "Form", "Contact details", "Map or service area", "FAQ"],
  },
  {
    id: "landing",
    name: "Campaign landing",
    description: "A focused, navigation-light lead-generation page.",
    sections: ["Offer hero", "Benefits", "Proof", "Objections", "Lead form"],
  },
  {
    id: "locations",
    name: "Locations",
    description: "Location index plus a repeatable local page pattern.",
    sections: ["Locations hero", "Location grid", "Service area", "Directions", "CTA"],
  },
];

const stylePresets: StylePreset[] = [
  {
    id: "editorial",
    name: "Clean editorial",
    description: "Quiet confidence, generous spacing and refined type.",
    colors: ["#17324D", "#F4EFE7", "#CB774F", "#FFFFFF"],
    heading: "Cormorant Garamond",
    body: "Outfit",
  },
  {
    id: "modern",
    name: "Modern studio",
    description: "Crisp structure, bold headlines and graphic contrast.",
    colors: ["#101828", "#F7F8FA", "#F05A3C", "#B8D8D8"],
    heading: "Manrope",
    body: "Inter",
  },
  {
    id: "warm",
    name: "Warm local",
    description: "Approachable, grounded and intentionally human.",
    colors: ["#29443B", "#F6F0E5", "#D99158", "#D8E2D7"],
    heading: "DM Serif Display",
    body: "DM Sans",
  },
  {
    id: "technical",
    name: "Technical minimal",
    description: "Precise, restrained and highly information-forward.",
    colors: ["#121B25", "#F3F5F7", "#3274F0", "#D6E4FF"],
    heading: "IBM Plex Sans",
    body: "IBM Plex Sans",
  },
];

const steps = ["Pages", "Brand", "Content", "Build"];

export function SiteConfigurator() {
  const [step, setStep] = useState(() => readBuildIdFromFragment() ? 3 : 0);
  const [selectedPages, setSelectedPages] = useState<string[]>([
    "home",
    "services",
    "about",
    "faq",
    "contact",
  ]);
  const [styleId, setStyleId] = useState("editorial");
  const [designAmbition, setDesignAmbition] = useState("signature");
  const [styleMode, setStyleMode] = useState("upload");
  const [styleUrl, setStyleUrl] = useState("");
  const [styleFiles, setStyleFiles] = useState<File[]>([]);
  const [siteName, setSiteName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#17324D");
  const [accentColor, setAccentColor] = useState("#CB774F");
  const [headingFont, setHeadingFont] = useState("Cormorant Garamond");
  const [bodyFont, setBodyFont] = useState("Outfit");
  const [context, setContext] = useState("");
  const [briefFiles, setBriefFiles] = useState<File[]>([]);
  const [audience, setAudience] = useState("");
  const [services, setServices] = useState("");
  const [tone, setTone] = useState("Clear, confident, human");
  const [primaryCtaLabel, setPrimaryCtaLabel] = useState("Get started");
  const [primaryCtaUrl, setPrimaryCtaUrl] = useState("/contact/");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagery, setImagery] = useState("generate");
  const [siteSlug, setSiteSlug] = useState("");
  const [requestedDomain, setRequestedDomain] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [buildAccessKey, setBuildAccessKey] = useState("");
  const [packageState, setPackageState] = useState<"idle" | "preparing" | "ready" | "error">("idle");
  const [preparedPackage, setPreparedPackage] = useState<PreparedPackage | null>(null);
  const [confirmedFingerprint, setConfirmedFingerprint] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<ProvisionerReadiness | null>(null);
  const [isCheckingReadiness, setIsCheckingReadiness] = useState(false);
  const [provisionJob, setProvisionJob] = useState<BuildJob | null>(null);
  const [oneTimeCredentials, setOneTimeCredentials] = useState<OneTimeCredentials | null>(null);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [provisionError, setProvisionError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeJobId, setResumeJobId] = useState<string | null>(() => readBuildIdFromFragment());

  const selected = useMemo(
    () => pageChoices.filter((page) => selectedPages.includes(page.id)),
    [selectedPages],
  );
  const selectedStyle = stylePresets.find((preset) => preset.id === styleId)!;
  const sectionCount = selected.reduce((sum, page) => sum + page.sections.length, 0);
  const resolvedSiteSlug = siteSlug || normalizeSiteSlug(siteName);
  const packageFingerprint = useMemo(() => JSON.stringify({
    selectedPages,
    styleId,
    designAmbition,
    styleMode,
    styleUrl,
    styleFiles: styleFiles.map(fileFingerprint),
    siteName,
    primaryColor,
    accentColor,
    headingFont,
    bodyFont,
    context,
    briefFiles: briefFiles.map(fileFingerprint),
    audience,
    services,
    tone,
    primaryCtaLabel,
    primaryCtaUrl,
    contactEmail,
    contactPhone,
    logoFile: logoFile ? fileFingerprint(logoFile) : null,
    imageFiles: imageFiles.map(fileFingerprint),
    imagery,
    siteSlug,
  }), [
    selectedPages, styleId, designAmbition, styleMode, styleUrl, styleFiles, siteName, primaryColor,
    accentColor, headingFont, bodyFont, context, briefFiles, audience, services, tone,
    primaryCtaLabel, primaryCtaUrl, contactEmail, contactPhone, logoFile, imageFiles,
    imagery, siteSlug,
  ]);
  const packageIsCurrent = packageState === "ready" && preparedPackage?.fingerprint === packageFingerprint;
  const resolvedDomain = requestedDomain.trim().toLowerCase() || `${resolvedSiteSlug}.dev-links.com`;
  const buildDetailsReady = isValidSiteSlug(resolvedSiteSlug)
    && isValidAdminEmail(adminEmail)
    && isValidDevLinksDomain(resolvedDomain)
    && resolvedDomain === `${resolvedSiteSlug}.dev-links.com`
    && buildAccessKey.trim().length > 0;
  const buildConfirmationKey = JSON.stringify({ packageFingerprint, resolvedDomain, adminEmail: adminEmail.trim().toLowerCase() });
  const buildConfirmed = confirmedFingerprint === buildConfirmationKey;
  const provisionIsActive = Boolean(provisionJob && ["queued", "running"].includes(provisionJob.state));
  const canStartProvision = Boolean(packageIsCurrent && buildDetailsReady && readiness?.ready && buildConfirmed && !provisionIsActive && !isSubmitting);
  const activeJobId = provisionJob?.id;
  const activeJobState = provisionJob?.state;
  const selectedSiteNeedsForm = selectedPages.some((page) => ["contact", "landing"].includes(page));
  const briefFilesAreValid = briefFiles.length <= MAX_BRIEF_FILES
    && briefFiles.every((file) => hasSupportedBriefFilename(file.name) && file.size <= MAX_BRIEF_FILE_BYTES);
  const stepReady = [
    selected.length > 0,
    siteName.trim().length > 1,
    (context.trim().length > 30 || briefFiles.length > 0)
      && briefFilesAreValid
      && audience.trim().length > 2
      && services.trim().length > 2
      && primaryCtaLabel.trim().length > 1
      && (!selectedSiteNeedsForm || isValidAdminEmail(contactEmail)),
    true,
  ][step];

  useEffect(() => {
    if (!activeJobId || !activeJobState || !["queued", "running"].includes(activeJobState) || !buildAccessKey) return;
    let stopped = false;
    const poll = async () => {
      try {
        const response = await fetch(`${BUILDS_API_PATH}/${encodeURIComponent(activeJobId)}`, {
          headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
          cache: "no-store",
        });
        const body: unknown = await response.json();
        if (!response.ok || !isBuildJob(body)) throw new Error(safeApiMessage(body, "The build status could not be read."));
        if (!stopped) {
          setProvisionJob(body);
          setProvisionError("");
        }
      } catch (error) {
        if (!stopped) setProvisionError(error instanceof Error ? error.message : "The build status could not be read.");
      }
    };
    const timer = window.setInterval(poll, 2500);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [activeJobId, activeJobState, buildAccessKey]);

  function togglePage(id: string) {
    if (id === "home") return;
    setSelectedPages((current) =>
      current.includes(id) ? current.filter((page) => page !== id) : [...current, id],
    );
  }

  function chooseStyle(preset: StylePreset) {
    setStyleId(preset.id);
    setPrimaryColor(preset.colors[0]);
    setAccentColor(preset.colors[2]);
    setHeadingFont(preset.heading);
    setBodyFont(preset.body);
  }

  async function preparePackage() {
    setPackageState("preparing");
    setProvisionError("");
    try {
      const prepared = await createBuildPackage();
      setPreparedPackage(prepared);
      setPackageState("ready");
    } catch (error) {
      setPackageState("error");
      setProvisionError(error instanceof Error ? error.message : "The build package could not be prepared.");
    }
  }

  async function createBuildPackage(): Promise<PreparedPackage> {
    validatePackageInputs({ siteName, context, briefFiles, audience, services, styleMode, styleUrl, styleFiles, logoFile, imageFiles });
    const packagedLogo = logoFile ? await prepareLogoForWordPress(logoFile) : null;
    const payload = {
      schema_version: 1,
      site: {
        key: resolvedSiteSlug,
        name: siteName,
        status: "draft",
        page_template: "elementor_header_footer",
      },
      design_system: {
        reference: {
          mode: styleMode,
          url: styleUrl,
          files: styleFiles.map((file) => `references/${file.name}`),
          instruction: "Analyze this reference as visual direction. Extract its layout grammar, hierarchy, spacing, typography traits, surfaces, image treatment, and interaction style without copying its brand or content.",
        },
        direction: selectedStyle.id,
        ambition: designAmbition,
        colors: { primary: primaryColor, accent: accentColor },
        typography: { heading: headingFont, body: bodyFont },
        palette_strategy: packagedLogo ? "derive_from_logo_then_add_accessible_complements" : "derive_from_reference_then_add_accessible_complements",
      },
      content_context: {
        summary: context,
        audience,
        services,
        tone,
        verified_facts_only: true,
        generation_rule: "Do not invent testimonials, statistics, credentials, pricing, locations, or regulated claims.",
      },
      content_sources: briefFiles.map((file, index) => ({
        key: `brief-${index + 1}`,
        source: `briefs/${file.name}`,
        label: file.name,
      })),
      brand: {
        logo: packagedLogo ? { key: "brand-logo", source: `assets/${packagedLogo.name}`, alt: `${siteName} logo` } : null,
        primary_cta: { label: primaryCtaLabel, url: primaryCtaUrl },
        email: contactEmail,
        phone: contactPhone,
      },
      imagery: {
        mode: imagery,
        generation_rule: "Create one cohesive art direction for the whole site. Generate purposeful page imagery, avoid generic stock-photo cliches, avoid embedded text and logos, and keep people and locations truthful to the supplied brief.",
        assets: imageFiles.map((file, index) => ({
          key: `site-image-${index + 1}`,
          source: `assets/${file.name}`,
          alt: "",
          rights_confirmed: false,
        })),
      },
      navigation: selected
        .filter((page) => page.id !== "landing")
        .map((page) => ({ label: page.name, url: page.id === "home" ? "/" : `/${page.id}/` })),
      footer: { summary: context, copyright: `${siteName}. All rights reserved.` },
      pages: selected.map((page) => ({
        type: page.id,
        title: page.name,
        slug: page.id === "home" ? "home" : page.id,
        sections: page.sections,
      })),
      generation: {
        create_global_kit: true,
        create_header_footer: true,
        create_navigation: true,
        composition_mode: "signature",
        entrance_effects: "none",
        import_status: "draft",
        ai_creative_direction: {
          enabled: true,
          model: "gpt-5.6",
          reasoning_effort: "high",
          analyze_references: true,
          derive_brand_palette: true,
          generate_page_copy: true,
          generate_images: imagery !== "provided",
          max_generated_images: Math.min(8, Math.max(3, selected.length + 1)),
          concept_count: 3,
          auto_select_concept: true,
          max_repair_passes: 2,
          design_ambition: designAmbition,
          visual_quality_review: true,
        },
      },
    };
    const entries: Record<string, Uint8Array> = {
      "project.json": strToU8(JSON.stringify(payload, null, 2)),
    };
    for (const file of styleFiles) entries[`references/${file.name}`] = new Uint8Array(await file.arrayBuffer());
    for (const file of briefFiles) entries[`briefs/${file.name}`] = new Uint8Array(await file.arrayBuffer());
    if (packagedLogo) entries[`assets/${packagedLogo.name}`] = new Uint8Array(await packagedLogo.arrayBuffer());
    for (const file of imageFiles) entries[`assets/${file.name}`] = new Uint8Array(await file.arrayBuffer());
    const zipped = zipSync(entries, { level: 6 });
    if (zipped.byteLength > 64 * 1024 * 1024) {
      throw new Error("The prepared build package exceeds 64 MB. Reduce the uploaded reference or source-image sizes.");
    }
    const hashInput = new Uint8Array(zipped.byteLength);
    hashInput.set(zipped);
    const digest = await crypto.subtle.digest("SHA-256", hashInput.buffer);
    const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    const filename = `${payload.site.key || "elementor-site"}-build-package.zip`;
    return {
      file: new File([zipped as BlobPart], filename, { type: "application/zip" }),
      fingerprint: packageFingerprint,
      sha256,
    };
  }

  function downloadPreparedPackage() {
    if (!preparedPackage || !packageIsCurrent) return;
    const url = URL.createObjectURL(preparedPackage.file);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = preparedPackage.file.name;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  async function checkReadiness() {
    if (!buildAccessKey.trim()) return;
    setIsCheckingReadiness(true);
    setProvisionError("");
    try {
      const response = await fetch(READINESS_API_PATH, {
        headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
        cache: "no-store",
      });
      const body: unknown = await response.json();
      if (!response.ok || !isProvisionerReadiness(body)) throw new Error(safeApiMessage(body, "The provisioner readiness check failed."));
      setReadiness(body);
      if (resumeJobId && !provisionJob) {
        const resumeResponse = await fetch(`${BUILDS_API_PATH}/${encodeURIComponent(resumeJobId)}`, {
          headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
          cache: "no-store",
        });
        const resumeBody: unknown = await resumeResponse.json();
        if (resumeResponse.status === 404) {
          forgetBuildId();
          setResumeJobId(null);
        } else if (!resumeResponse.ok || !isBuildJob(resumeBody)) {
          throw new Error(safeApiMessage(resumeBody, "The saved build could not be resumed."));
        } else {
          setProvisionJob(resumeBody);
        }
      }
    } catch (error) {
      setReadiness(null);
      setProvisionError(error instanceof Error ? error.message : "The provisioner readiness check failed.");
    } finally {
      setIsCheckingReadiness(false);
    }
  }

  async function startProvision() {
    if (!canStartProvision || !preparedPackage) return;
    setIsSubmitting(true);
    setProvisionError("");
    setOneTimeCredentials(null);
    const requestId = crypto.randomUUID();
    setResumeJobId(requestId);
    rememberBuildId(requestId);
    try {
      const response = await fetch(BUILDS_API_PATH, {
        method: "POST",
        headers: {
          "Content-Type": "application/zip",
          "X-Site-Domain": resolvedDomain,
          "X-Site-Name": encodeURIComponent(siteName.trim()),
          "X-Admin-Email": adminEmail.trim(),
          "X-Build-Request-Id": requestId,
          ...bearerHeaders(buildAccessKey.trim()),
        },
        body: preparedPackage.file,
      });
      const body: unknown = await response.json();
      if (!response.ok || !isBuildJob(body)) throw new Error(safeApiMessage(body, "The site build could not be started."));
      setProvisionJob(body);
      setResumeJobId(body.id);
      rememberBuildId(body.id);
      setConfirmedFingerprint(null);
    } catch (error) {
      setProvisionError(error instanceof Error ? error.message : "The site build could not be started.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function revealCredentials() {
    if (!provisionJob || provisionJob.state !== "succeeded" || !buildAccessKey) return;
    setIsLoadingCredentials(true);
    setProvisionError("");
    try {
      const response = await fetch(`${BUILDS_API_PATH}/${encodeURIComponent(provisionJob.id)}/credentials`, {
        headers: { Accept: "application/json", ...bearerHeaders(buildAccessKey.trim()) },
        cache: "no-store",
      });
      const body: unknown = await response.json();
      if (!response.ok || !isOneTimeCredentials(body)) throw new Error(safeApiMessage(body, "The one-time login could not be retrieved."));
      setOneTimeCredentials(body);
    } catch (error) {
      setProvisionError(error instanceof Error ? error.message : "The one-time login could not be retrieved.");
    } finally {
      setIsLoadingCredentials(false);
    }
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <a className="brand-lockup" href="#top" aria-label="Site Foundry home">
          <span className="brand-mark">SF</span>
          <span>
            <strong>Site Foundry</strong>
            <small>Elementor suite builder</small>
          </span>
        </a>
        <div className="topbar-status">
          <span className="status-dot" />
          Hetzner build pipeline ready
        </div>
      </header>

      <div className="workspace" id="top">
        <aside className="step-rail" aria-label="Builder progress">
          <p className="rail-label">Build setup</p>
          <ol>
            {steps.map((label, index) => (
              <li key={label}>
                <button
                  className={index === step ? "active" : index < step ? "complete" : ""}
                  disabled={provisionIsActive}
                  onClick={() => setStep(index)}
                  type="button"
                >
                  <span>{index < step ? "✓" : index + 1}</span>
                  {label}
                </button>
              </li>
            ))}
          </ol>
          <div className="rail-note">
            <strong>What one run creates</strong>
            <p>Global styles, header, footer, navigation, responsive pages, reusable sections and a published noindex preview.</p>
          </div>
        </aside>

        <section className="builder-panel">
          {step === 0 && (
            <div className="step-content">
              <div className="section-heading">
                <p className="eyebrow">01 · Choose the site</p>
                <h1>Which pages do you need?</h1>
                <p>Select the pages. The system chooses the right sections and builds a complete first-pass sitemap.</p>
              </div>

              <div className="selection-bar">
                <span><strong>{selected.length}</strong> pages selected</span>
                <span>{sectionCount} planned sections</span>
                <button
                  type="button"
                  onClick={() => setSelectedPages(["home", "services", "about", "faq", "contact"])}
                >
                  Use recommended 5-page set
                </button>
              </div>

              <div className="page-grid">
                {pageChoices.map((page) => {
                  const isSelected = selectedPages.includes(page.id);
                  return (
                    <button
                      key={page.id}
                      type="button"
                      className={`page-choice ${isSelected ? "selected" : ""}`}
                      onClick={() => togglePage(page.id)}
                      aria-pressed={isSelected}
                    >
                      <span className="checkmark">{isSelected ? "✓" : ""}</span>
                      <span className="page-choice-copy">
                        <span className="page-choice-title">
                          <strong>{page.name}</strong>
                          {page.required && <small>Required</small>}
                        </span>
                        <span>{page.description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="step-content">
              <div className="section-heading">
                <p className="eyebrow">02 · Give it the style</p>
                <h1>Show us what it should feel like.</h1>
                <p>Add mockup screenshots, a direct image URL, or an Elementor JSON export. The creative-director pass reads that visual system and uses it to drive the actual build.</p>
              </div>

              <div className="reference-panel">
                <div className="reference-tabs" role="tablist" aria-label="Style reference type">
                  {[
                    ["upload", "Upload reference"],
                    ["url", "Image URL"],
                    ["elementor", "Elementor template"],
                  ].map(([value, label]) => (
                    <button
                      type="button"
                      key={value}
                      className={styleMode === value ? "active" : ""}
                      onClick={() => setStyleMode(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {styleMode === "upload" && (
                  <label className="reference-drop">
                    <input
                      type="file"
                      multiple
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(event) => setStyleFiles(Array.from(event.target.files || []))}
                    />
                    <span className="upload-glyph">＋</span>
                    <span>
                      <strong>{styleFiles.length ? `${styleFiles.length} reference${styleFiles.length === 1 ? "" : "s"} added` : "Drop mockups or screenshots here"}</strong>
                      <small>{styleFiles.length ? styleFiles.map((file) => file.name).join(", ") : "PNG, JPG, or WebP · multiple screens are welcome"}</small>
                    </span>
                    <b>{styleFiles.length ? "Replace" : "Browse"}</b>
                  </label>
                )}

                {styleMode === "url" && (
                  <label className="field reference-url">
                    <span>Direct screenshot or mockup URL</span>
                    <input placeholder="https://…/mockup.png" value={styleUrl} onChange={(event) => setStyleUrl(event.target.value)} />
                    <small>Use a public PNG, JPG, or WebP URL. For a live site or Figma page, upload screenshots so the visual pass can inspect the pixels.</small>
                  </label>
                )}

                {styleMode === "elementor" && (
                  <label className="reference-drop">
                    <input
                      type="file"
                      accept="application/json,.json"
                      onChange={(event) => setStyleFiles(Array.from(event.target.files || []))}
                    />
                    <span className="upload-glyph">↥</span>
                    <span>
                      <strong>{styleFiles.length ? styleFiles[0].name : "Add an Elementor kit or page export"}</strong>
                      <small>The cleanest route when the reference already exists in Elementor</small>
                    </span>
                    <b>{styleFiles.length ? "Replace" : "Browse"}</b>
                  </label>
                )}

                <div className="extraction-row">
                  <span>Design decisions to capture</span>
                  <ul>
                    <li>Colors + type</li>
                    <li>Spacing + widths</li>
                    <li>Buttons + cards</li>
                    <li>Image treatment</li>
                    <li>Section rhythm</li>
                  </ul>
                </div>
              </div>

              <label className="field field-wide">
                <span>Site or company name</span>
                <input value={siteName} onChange={(event) => setSiteName(event.target.value)} />
              </label>

              <div className="fallback-label"><span>Fallback direction</span><small>The AI starts here only when the logo or reference does not answer a design decision.</small></div>
              <div className="style-grid">
                {stylePresets.map((preset) => (
                  <button
                    type="button"
                    key={preset.id}
                    onClick={() => chooseStyle(preset)}
                    className={`style-card ${preset.id === styleId ? "selected" : ""}`}
                    aria-pressed={preset.id === styleId}
                  >
                    <span className="palette">
                      {preset.colors.map((color) => <i key={color} style={{ background: color }} />)}
                    </span>
                    <strong>{preset.name}</strong>
                    <span>{preset.description}</span>
                    <small>{preset.heading} + {preset.body}</small>
                  </button>
                ))}
              </div>

              <fieldset className="image-options design-ambition">
                <legend>Design ambition</legend>
                {[
                  ["refined", "Refined", "Restrained, elegant and highly polished with subtle composition shifts."],
                  ["signature", "Signature", "Distinctive and classy with stronger typography, asymmetry and page-specific layouts."],
                  ["experimental", "Experimental", "The most daring native-Elementor direction while preserving usability and editability."],
                ].map(([value, label, description]) => (
                  <label key={value} className={designAmbition === value ? "selected" : ""}>
                    <input
                      type="radio"
                      name="design-ambition"
                      value={value}
                      checked={designAmbition === value}
                      onChange={() => setDesignAmbition(value)}
                    />
                    <span><strong>{label}</strong><small>{description}</small></span>
                  </label>
                ))}
              </fieldset>

              <div className="field-grid brand-fields">
                <label className="field color-field">
                  <span>Primary color</span>
                  <span className="color-control">
                    <input type="color" value={primaryColor} onChange={(event) => setPrimaryColor(event.target.value)} />
                    <input value={primaryColor} onChange={(event) => setPrimaryColor(event.target.value)} />
                  </span>
                </label>
                <label className="field color-field">
                  <span>Accent color</span>
                  <span className="color-control">
                    <input type="color" value={accentColor} onChange={(event) => setAccentColor(event.target.value)} />
                    <input value={accentColor} onChange={(event) => setAccentColor(event.target.value)} />
                  </span>
                </label>
                <label className="field">
                  <span>Heading font</span>
                  <select value={headingFont} onChange={(event) => setHeadingFont(event.target.value)}>
                    {["Cormorant Garamond", "Manrope", "DM Serif Display", "IBM Plex Sans", "Libre Baskerville"].map((font) => <option key={font}>{font}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span>Body font</span>
                  <select value={bodyFont} onChange={(event) => setBodyFont(event.target.value)}>
                    {["Outfit", "Inter", "DM Sans", "IBM Plex Sans", "Source Sans 3"].map((font) => <option key={font}>{font}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <div className="section-heading">
                <p className="eyebrow">03 · Give it context</p>
                <h1>Describe the business.</h1>
                <p>Give it the real facts. The creative-director pass turns them into complete, page-specific copy and an image system without inventing claims.</p>
              </div>

              <div className="field-grid content-fields">
                <label className="field field-wide">
                  <span>What does the business do?</span>
                  <textarea rows={5} placeholder="Paste the client brief, discovery notes, or a rough description…" value={context} onChange={(event) => setContext(event.target.value)} />
                  <small>Paste a client brief, discovery notes or a rough paragraph — or attach source files below.</small>
                </label>
                <label className="asset-upload field-wide">
                  <input type="file" multiple accept=".docx,.txt,.md,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(event) => setBriefFiles(Array.from(event.target.files || []))} />
                  <span>
                    <strong>{briefFiles.length ? `${briefFiles.length} source brief${briefFiles.length === 1 ? "" : "s"} added` : "Add source briefs or SOWs"}</strong>
                    <small>{briefFiles.length ? briefFiles.map((file) => file.name).join(", ") : "DOCX, TXT, or Markdown · up to five files"}</small>
                  </span>
                  <b>{briefFiles.length ? "Replace" : "Browse"}</b>
                </label>
                <label className="field">
                  <span>Primary audience</span>
                  <input placeholder="Who is this site for?" value={audience} onChange={(event) => setAudience(event.target.value)} />
                </label>
                <label className="field">
                  <span>Tone</span>
                  <input value={tone} onChange={(event) => setTone(event.target.value)} />
                </label>
                <label className="field field-wide">
                  <span>Core services or offers</span>
                  <input placeholder="List the real services the site should cover" value={services} onChange={(event) => setServices(event.target.value)} />
                </label>
                <label className="field">
                  <span>Primary action label</span>
                  <input value={primaryCtaLabel} onChange={(event) => setPrimaryCtaLabel(event.target.value)} />
                </label>
                <label className="field">
                  <span>Primary action URL</span>
                  <input value={primaryCtaUrl} onChange={(event) => setPrimaryCtaUrl(event.target.value)} />
                </label>
                <label className="field">
                  <span>Contact email</span>
                  <input type="email" placeholder="hello@example.com" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} />
                </label>
                <label className="field">
                  <span>Contact phone</span>
                  <input placeholder="Optional" value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
                </label>
              </div>

              <fieldset className="image-options">
                <legend>Imagery</legend>
                {[
                  ["generate", "Generate the complete image system", "Create coordinated hero and supporting imagery from the brand, brief, and visual direction."],
                  ["hybrid", "Use uploads and generate the gaps", "Keep approved client imagery, then create a matching set for the remaining placements."],
                  ["provided", "Use only provided images", "Use the client’s approved assets without generating additional imagery."],
                ].map(([value, label, description]) => (
                  <label key={value} className={imagery === value ? "selected" : ""}>
                    <input type="radio" name="imagery" value={value} checked={imagery === value} onChange={() => setImagery(value)} />
                    <span><strong>{label}</strong><small>{description}</small></span>
                  </label>
                ))}
              </fieldset>

              <div className="asset-row">
                <label className="asset-upload">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg" onChange={(event) => setLogoFile(event.target.files?.[0] || null)} />
                  <span><strong>{logoFile ? logoFile.name : "Add logo"}</strong><small>SVG is safely converted to a transparent PNG automatically · PNG, JPG, and WebP stay unchanged</small></span>
                  <b>{logoFile ? "Replace" : "Browse"}</b>
                </label>
                <label className="asset-upload">
                  <input type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif,image/avif" onChange={(event) => setImageFiles(Array.from(event.target.files || []))} />
                  <span><strong>{imageFiles.length ? `${imageFiles.length} image${imageFiles.length === 1 ? "" : "s"} added` : "Add approved source imagery"}</strong><small>{imageFiles.length ? imageFiles.map((file) => file.name).join(", ") : "PNG, JPG, WebP, GIF or AVIF · optional when AI image generation is enabled"}</small></span>
                  <b>{imageFiles.length ? "Replace" : "Browse"}</b>
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content build-step">
              <div className="section-heading">
                <p className="eyebrow">04 · Build the site</p>
                <h1>Review, provision, and hand off.</h1>
                <p>The server analyzes the reference and logo, writes the site, creates its imagery, builds WordPress, and verifies the finished Elementor site.</p>
              </div>

              <div className="build-summary">
                <div><strong>{selected.length}</strong><span>Pages</span></div>
                <div><strong>{sectionCount}</strong><span>Sections</span></div>
                <div><strong>2</strong><span>Global templates</span></div>
                <div><strong>1</strong><span>Elementor kit</span></div>
              </div>

              <section className="provision-card" aria-labelledby="build-target-heading">
                <div className="provision-card-heading">
                  <div>
                    <span>Build target</span>
                    <h2 id="build-target-heading">Where should this site live?</h2>
                  </div>
                  <b>Hetzner</b>
                </div>
                <div className="field-grid provision-fields">
                  <label className="field">
                    <span>Site slug</span>
                    <input
                      value={siteSlug}
                      placeholder={normalizeSiteSlug(siteName) || "client-site"}
                      disabled={provisionIsActive}
                      onChange={(event) => setSiteSlug(normalizeSiteSlug(event.target.value))}
                    />
                    <small>Used as the WordPress site key and default subdomain.</small>
                  </label>
                  <label className="field">
                    <span>Domain</span>
                    <input
                      value={requestedDomain}
                      placeholder={`${resolvedSiteSlug || "client-site"}.dev-links.com`}
                      disabled={provisionIsActive}
                      onChange={(event) => setRequestedDomain(event.target.value.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, ""))}
                    />
                    <small>V1 accepts one <code>*.dev-links.com</code> hostname.</small>
                  </label>
                  <label className="field">
                    <span>WordPress admin email</span>
                    <input
                      type="email"
                      value={adminEmail}
                      placeholder="owner@example.com"
                      disabled={provisionIsActive}
                      onChange={(event) => setAdminEmail(event.target.value)}
                    />
                    <small>Receives the site handoff; it is not written into the project ZIP.</small>
                  </label>
                  <label className="field">
                    <span>Build access key</span>
                    <input
                      type="password"
                      value={buildAccessKey}
                      placeholder="Required to reach the provisioner"
                      autoComplete="off"
                      disabled={provisionIsActive}
                      onChange={(event) => {
                        setBuildAccessKey(event.target.value);
                        setReadiness(null);
                      }}
                    />
                    <small>Held in memory for this tab only. Never saved or added to the package.{resumeJobId ? " A saved build ID is ready to resume when you check the server." : ""}</small>
                  </label>
                </div>

                <div className="readiness-row">
                  <div>
                    <strong>Provisioner readiness</strong>
                    <small>{readiness ? readiness.message : "Authenticate and verify the server before starting a real build."}</small>
                  </div>
                  <span className={`readiness-badge ${readiness?.ready ? "ready" : readiness ? "blocked" : "unchecked"}`}>
                    {readiness?.ready ? "Ready" : readiness ? "Blocked" : "Not checked"}
                  </span>
                  <button type="button" onClick={checkReadiness} disabled={!buildAccessKey || isCheckingReadiness || provisionIsActive}>
                    {isCheckingReadiness ? "Checking…" : readiness ? "Check again" : "Check server"}
                  </button>
                </div>
                {readiness && (
                  <ul className="readiness-checks">
                    {readiness.checks.map((check) => (
                      <li key={check.id} className={check.status}>
                        <span>{check.status === "pass" ? "✓" : check.status === "warning" ? "!" : "×"}</span>
                        <p><strong>{check.label}</strong><small>{check.message}</small></p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="package-card" aria-labelledby="package-heading">
                <div>
                  <span className={`package-mark ${packageIsCurrent ? "ready" : ""}`}>{packageIsCurrent ? "✓" : "1"}</span>
                  <p>
                    <strong id="package-heading">Compiler package</strong>
                    <small>
                      {packageIsCurrent && preparedPackage
                        ? `${formatBytes(preparedPackage.file.size)} · SHA-256 ${preparedPackage.sha256.slice(0, 12)}…`
                        : preparedPackage ? "Inputs changed. Prepare the package again before building." : "Package the brief, references, logo, and imagery locally in this browser."}
                    </small>
                  </p>
                </div>
                <div className="package-actions">
                  {packageIsCurrent && <button type="button" className="quiet-button" onClick={downloadPreparedPackage}>Download copy</button>}
                  <button
                    type="button"
                    className="prepare-button"
                    onClick={preparePackage}
                    disabled={!isValidSiteSlug(resolvedSiteSlug) || packageState === "preparing" || provisionIsActive}
                  >
                    {packageState === "preparing" ? "Preparing…" : packageIsCurrent ? "Prepare again" : "Prepare package"}
                  </button>
                </div>
              </section>

              {!provisionJob && (
                <div className="build-confirmation">
                  <label>
                    <input
                      type="checkbox"
                      checked={buildConfirmed}
                      disabled={!packageIsCurrent || provisionIsActive}
                      onChange={(event) => setConfirmedFingerprint(event.target.checked ? buildConfirmationKey : null)}
                    />
                    <span>
                      <strong>Create a real WordPress site on Hetzner</strong>
                      <small>I confirm the domain and admin email above. This runs the AI creative build, publishes a public noindex preview, and activates its form notifications.</small>
                    </span>
                  </label>
                  <button className="build-button" type="button" onClick={startProvision} disabled={!canStartProvision}>
                    {isSubmitting ? "Starting build…" : "Build site"}
                  </button>
                  {!canStartProvision && (
                    <p className="build-requirements">Required: valid slug/domain/email, access key, ready server, current package, and confirmation.</p>
                  )}
                </div>
              )}

              {provisionError && <div className="provision-error" role="alert">{provisionError}</div>}

              {provisionJob && (
                <section className="job-card" aria-live="polite" aria-labelledby="job-heading">
                  {/** Any completed visual miss remains a verified noindex staging handoff. */}
                  {(() => {
                    const needsPolish = provisionJob.state === "succeeded" && provisionJob.result?.visual_qa_disposition === "needs_polish";
                    const visualBlocking = needsPolish && ((provisionJob.result?.visual_qa_critical_issue_count || 0) > 0 || (provisionJob.result?.visual_qa_high_issue_count || 0) > 0);
                    return <>
                  <div className="job-heading">
                    <div>
                      <span>Build {provisionJob.id}</span>
                      <h2 id="job-heading">{visualBlocking ? "Your noindex staging site needs visual repair." : needsPolish ? "Your site is live and needs polish." : provisionJob.state === "succeeded" ? "Your site is ready." : provisionJob.state === "failed" ? "The build stopped." : "Building your site…"}</h2>
                      <p>{provisionJob.message}</p>
                    </div>
                    <b className={`job-state ${needsPolish ? "needs-polish" : provisionJob.state}`}>{visualBlocking ? "visual repair needed" : needsPolish ? "needs polish" : provisionJob.state.replace("_", " ")}</b>
                  </div>

                  <ol className="stage-list">
                    {provisionJob.stages.map((stage) => (
                      <li key={stage.id} className={stage.state}>
                        <span>{stage.state === "succeeded" ? "✓" : stage.state === "failed" ? "×" : stage.state === "running" ? "•" : ""}</span>
                        <p><strong>{stage.label}</strong>{stage.message && <small>{stage.message}</small>}</p>
                      </li>
                    ))}
                  </ol>

                  {provisionJob.state === "failed" && (
                    <div className="job-retry">
                      <button type="button" className="quiet-button" onClick={() => { setProvisionJob(null); setProvisionError(""); setResumeJobId(null); forgetBuildId(); }}>
                        Retry build
                      </button>
                    </div>
                  )}

                  {provisionJob.state === "succeeded" && provisionJob.result && (
                    <div className="handoff-card">
                      <div>
                        <strong>Site handoff</strong>
                        <p>
                          {needsPolish
                            ? visualBlocking
                              ? "The noindex staging site is online and retained for in-place repair; it was not rebuilt or rolled back, but it is not launch-ready."
                              : "The noindex staging site is online and retained for refinement; it was not rebuilt or rolled back."
                            : "The site is online and the generated Elementor pages are ready for review in WordPress."}
                          {typeof provisionJob.result.visual_qa_score === "number" && ` Visual QA: ${provisionJob.result.visual_qa_score}/100${provisionJob.result.visual_qa_disposition === "needs_polish" ? " needs polish" : provisionJob.result.visual_qa_passed ? " passed" : " with improvement notes saved"}.`}
                        </p>
                        {needsPolish && <p><small>{visualBlocking
                          ? `Everything deterministic—including WordPress, Elementor, media, forms, HTTPS, and noindex—passed. Visual QA still reports ${provisionJob.result.visual_qa_critical_issue_count || 0} critical and ${provisionJob.result.visual_qa_high_issue_count || 0} high issue(s), so this staging site needs in-place repair before launch.`
                          : "Everything deterministic—including WordPress, Elementor, media, forms, HTTPS, and noindex—passed. The remaining notes are aesthetic only."}</small></p>}
                        {typeof provisionJob.result.video_verify_checked === "number" && provisionJob.result.video_verify_checked > 0 && <p><small>Native hosted video verification: {provisionJob.result.video_verify_checked} checked{provisionJob.result.video_verify_ok ? ", passed." : ", needs attention."}</small></p>}
                        {!provisionJob.result.visual_qa_passed && provisionJob.result.visual_qa_summary && (
                          <p><small>{provisionJob.result.visual_qa_summary}</small></p>
                        )}
                        {!provisionJob.result.visual_qa_passed && provisionJob.result.visual_qa_repairs?.length ? (
                          <ul>{provisionJob.result.visual_qa_repairs.map((repair, index) => <li key={`${index}-${repair}`}>{repair}</li>)}</ul>
                        ) : null}
                      </div>
                      <div className="handoff-links">
                        <a href={provisionJob.result.site_url} target="_blank" rel="noreferrer">Open site</a>
                        {provisionJob.result.admin_url && <a href={provisionJob.result.admin_url} target="_blank" rel="noreferrer">Open WordPress</a>}
                        {!oneTimeCredentials && (
                          <button type="button" onClick={revealCredentials} disabled={isLoadingCredentials}>
                            {isLoadingCredentials ? "Retrieving…" : "Reveal one-time login"}
                          </button>
                        )}
                      </div>
                      {oneTimeCredentials && (
                        <div className="credential-card">
                          <p><strong>Save this login now.</strong><small>It is shown once and held only in this tab&apos;s memory. Refreshing clears it.</small></p>
                          <dl>
                            <div><dt>Admin URL</dt><dd><a href={oneTimeCredentials.admin_url} target="_blank" rel="noreferrer">{oneTimeCredentials.admin_url}</a></dd></div>
                            <div><dt>Username</dt><dd><code>{oneTimeCredentials.username}</code></dd></div>
                            <div><dt>Password</dt><dd><code>{oneTimeCredentials.password}</code></dd></div>
                          </dl>
                        </div>
                      )}
                    </div>
                  )}
                    </>;
                  })()}
                </section>
              )}
            </div>
          )}

          <div className="builder-actions">
            <button type="button" className="back-button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0 || provisionIsActive}>Back</button>
            {step < 3 && (
              <div className="continue-wrap">
                {!stepReady && <small>{step === 1 ? "Add the site name to continue." : step === 2 && !briefFilesAreValid ? "Use up to five DOCX, TXT, or Markdown source briefs under 5 MB each." : selectedSiteNeedsForm && !isValidAdminEmail(contactEmail) ? "Add the form notification email to continue." : "Add a business summary or source briefs to continue."}</small>}
                <button type="button" className="continue-button" disabled={!stepReady} onClick={() => setStep(step + 1)}>Continue <span>→</span></button>
              </div>
            )}
          </div>
        </section>

        <aside className="live-plan">
          <div className="live-plan-header">
            <div>
              <p className="rail-label">Illustrative style preview</p>
              <h2>{siteName || "Untitled site"}</h2>
            </div>
            <span>{selected.length} pages</span>
          </div>

          <div className="mini-browser" style={{ "--preview-primary": primaryColor, "--preview-accent": accentColor } as React.CSSProperties}>
            <div className="browser-bar"><i /><i /><i /></div>
            <div className="browser-nav"><strong>{(siteName || "Site").slice(0, 18)}</strong><span /><span /><button>Get started</button></div>
            <div className="browser-hero">
              <div><small>{selectedStyle.name}</small><strong>A clear headline that earns attention.</strong><span /><span className="short" /><button>Primary action</button></div>
              <i />
            </div>
            <div className="browser-cards"><i /><i /><i /></div>
          </div>

          <div className="sitemap-list">
            {selected.map((page, index) => (
              <details key={page.id} open={index === 0}>
                <summary><span>{String(index + 1).padStart(2, "0")}</span><strong>{page.name}</strong><small>{page.sections.length} sections</small></summary>
                <ul>{page.sections.map((section) => <li key={section}>{section}</li>)}</ul>
              </details>
            ))}
          </div>
        </aside>
      </div>
    </main>
  );
}

function fileFingerprint(file: File) {
  return [file.name, file.size, file.type, file.lastModified];
}

const MAX_SVG_LOGO_BYTES = 5 * 1024 * 1024;
const SVG_LOGO_RASTER_SIDE = 1600;
const SVG_LOGO_MIN_LONG_SIDE = 1024;
const MAX_AI_VISUAL_BYTES = 8 * 1024 * 1024;
const MAX_SOURCE_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_BRIEF_FILES = 5;
const MAX_BRIEF_FILE_BYTES = 5 * 1024 * 1024;
const FORBIDDEN_SVG_ELEMENTS = new Set([
  "animate",
  "animatemotion",
  "animatetransform",
  "audio",
  "canvas",
  "embed",
  "foreignobject",
  "iframe",
  "object",
  "script",
  "set",
  "video",
]);

async function prepareLogoForWordPress(file: File): Promise<File> {
  if (!isSvgFile(file)) {
    if (!isSupportedRasterLogo(file)) {
      throw new Error("Logo files must be PNG, JPG, WebP, or SVG so both WordPress and the visual creative pass can read them.");
    }
    return file;
  }
  if (file.size > MAX_SVG_LOGO_BYTES) {
    throw new Error("The SVG logo is too large to convert safely. Use an SVG under 5 MB or upload a PNG, JPG, or WebP file.");
  }

  const source = await file.text();
  if (/<!DOCTYPE|<!ENTITY/i.test(source)) {
    throw new Error("The SVG logo contains unsupported document declarations. Export a plain SVG or upload a PNG, JPG, or WebP file.");
  }

  const parsed = new DOMParser().parseFromString(source, "image/svg+xml");
  const root = parsed.documentElement;
  if (root.localName.toLowerCase() !== "svg" || parsed.querySelector("parsererror")) {
    throw new Error("The logo is not a valid SVG. Export it again or upload a PNG, JPG, or WebP file.");
  }

  sanitizeSvgTree(root);
  const { width, height } = svgRasterDimensions(root);
  root.setAttribute("width", String(width));
  root.setAttribute("height", String(height));

  const markup = new XMLSerializer().serializeToString(root);
  const svgBlob = new Blob([markup], { type: "image/svg+xml;charset=utf-8" });
  const objectUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await loadRasterImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser could not prepare the SVG logo.");
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const png = await canvasToPng(canvas);
    return new File([png], rasterizedLogoName(file.name), {
      type: "image/png",
      lastModified: file.lastModified,
    });
  } catch (error) {
    if (error instanceof Error && error.message !== "The SVG logo could not be rendered safely.") throw error;
    throw new Error("The SVG logo could not be converted. Export it as a plain SVG or upload a PNG, JPG, or WebP file.");
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function isSvgFile(file: File) {
  return file.type.toLowerCase() === "image/svg+xml" || /\.svg$/i.test(file.name);
}

function isSupportedRasterLogo(file: File) {
  return ["image/png", "image/jpeg", "image/webp"].includes(file.type.toLowerCase())
    || /\.(?:png|jpe?g|webp)$/i.test(file.name);
}

function validatePackageInputs(input: {
  siteName: string;
  context: string;
  briefFiles: File[];
  audience: string;
  services: string;
  styleMode: string;
  styleUrl: string;
  styleFiles: File[];
  logoFile: File | null;
  imageFiles: File[];
}) {
  if (input.siteName.trim().length < 2 || input.siteName.trim().length > 120) throw new Error("Site name must be 2–120 characters.");
  if (input.context.length > 8_000 || input.audience.length > 2_000 || input.services.length > 8_000) {
    throw new Error("The content brief is too large. Keep the business summary and services under 8,000 characters and the audience under 2,000.");
  }
  if (input.briefFiles.length > MAX_BRIEF_FILES) throw new Error("Use at most five source briefs per build.");
  const briefNames = new Set<string>();
  for (const file of input.briefFiles) {
    if (!hasSupportedBriefFilename(file.name)) throw new Error(`Brief ${file.name || "(unnamed)"} must be a DOCX, TXT, or Markdown file with a plain filename.`);
    const normalizedName = file.name.toLowerCase();
    if (briefNames.has(normalizedName)) throw new Error(`Brief filename ${file.name} is duplicated.`);
    briefNames.add(normalizedName);
    if (file.size > MAX_BRIEF_FILE_BYTES) throw new Error(`Brief ${file.name} exceeds the 5 MB per-file limit.`);
  }
  if (input.styleMode === "url" && input.styleUrl.trim() && !isDirectRasterUrl(input.styleUrl)) {
    throw new Error("The style URL must point directly to a public PNG, JPG, or WebP image. Upload screenshots for live sites or Figma pages.");
  }
  if (input.styleFiles.length > 6) throw new Error("Use at most six visual reference files per build.");
  for (const file of input.styleFiles) {
    if (input.styleMode === "elementor" && !/\.json$/i.test(file.name)) throw new Error("Elementor references must be JSON exports.");
    if (input.styleMode === "upload" && !/\.(?:png|jpe?g|webp)$/i.test(file.name)) throw new Error("Visual references must be PNG, JPG, or WebP files.");
    if (file.size > MAX_AI_VISUAL_BYTES) throw new Error(`Reference ${file.name} exceeds the 8 MB per-file limit.`);
  }
  if (input.logoFile && input.logoFile.size > MAX_AI_VISUAL_BYTES) throw new Error("The logo exceeds the 8 MB visual-analysis limit.");
  for (const file of input.imageFiles) {
    if (file.size > MAX_SOURCE_IMAGE_BYTES) throw new Error(`Source image ${file.name} exceeds the 20 MB per-file limit.`);
  }
}

function hasSupportedBriefFilename(filename: string) {
  return /^[^\\/\0]+\.(?:docx|txt|md)$/i.test(filename) && filename === filename.trim();
}

function isDirectRasterUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && /\.(?:png|jpe?g|webp)$/i.test(url.pathname);
  } catch {
    return false;
  }
}

function readBuildIdFromFragment() {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("build");
  return value && /^[a-f0-9-]{36}$/.test(value) ? value : null;
}

function rememberBuildId(id: string) {
  if (typeof window === "undefined" || !/^[a-f0-9-]{36}$/.test(id)) return;
  const values = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  values.set("build", id);
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${values.toString()}`);
}

function forgetBuildId() {
  if (typeof window === "undefined") return;
  const values = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  values.delete("build");
  const fragment = values.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${fragment ? `#${fragment}` : ""}`);
}

function sanitizeSvgTree(root: Element) {
  for (const element of [root, ...Array.from(root.querySelectorAll("*"))]) {
    if (element !== root && FORBIDDEN_SVG_ELEMENTS.has(element.localName.toLowerCase())) {
      element.remove();
      continue;
    }

    if (element.localName.toLowerCase() === "style" && hasUnsafeSvgCss(element.textContent || "")) {
      element.remove();
      continue;
    }

    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim();
      if (name.startsWith("on") || name === "xml:base" || /javascript\s*:/i.test(value)) {
        element.removeAttribute(attribute.name);
        continue;
      }
      if ((name === "href" || name === "xlink:href" || name === "src") && !isSafeSvgReference(value)) {
        element.removeAttribute(attribute.name);
        continue;
      }
      if ((name === "style" && hasUnsafeSvgCss(value)) || hasExternalCssUrl(value)) {
        element.removeAttribute(attribute.name);
      }
    }
  }
}

function isSafeSvgReference(value: string) {
  return value.startsWith("#") || /^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(value);
}

function hasUnsafeSvgCss(value: string) {
  return /@import|@font-face|expression\s*\(|javascript\s*:|behavior\s*:|-moz-binding/i.test(value)
    || hasExternalCssUrl(value);
}

function hasExternalCssUrl(value: string) {
  const containsUrl = /url\s*\(/i.test(value);
  if (!containsUrl) return false;
  const references = Array.from(value.matchAll(/url\s*\(\s*(["']?)(.*?)\1\s*\)/gi));
  return references.length === 0 || references.some((match) => !match[2].trim().startsWith("#"));
}

function svgRasterDimensions(root: Element) {
  const viewBox = (root.getAttribute("viewBox") || "")
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const viewBoxWidth = viewBox.length === 4 && Number.isFinite(viewBox[2]) && viewBox[2] > 0 ? viewBox[2] : null;
  const viewBoxHeight = viewBox.length === 4 && Number.isFinite(viewBox[3]) && viewBox[3] > 0 ? viewBox[3] : null;
  const naturalWidth = svgLength(root.getAttribute("width")) || viewBoxWidth || 1024;
  const naturalHeight = svgLength(root.getAttribute("height")) || viewBoxHeight || 1024;
  const ratio = Math.min(100, Math.max(0.01, naturalWidth / naturalHeight));
  const naturalLongSide = Math.max(naturalWidth, naturalHeight);
  const longSide = Math.min(SVG_LOGO_RASTER_SIDE, Math.max(SVG_LOGO_MIN_LONG_SIDE, naturalLongSide));
  const width = ratio >= 1 ? longSide : longSide * ratio;
  const height = ratio >= 1 ? longSide / ratio : longSide;
  return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
}

function svgLength(value: string | null) {
  const match = value?.match(/^\s*(\d+(?:\.\d+)?|\.\d+)(?:px)?\s*$/i);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function loadRasterImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The SVG logo could not be rendered safely."));
    image.src = source;
  });
}

function canvasToPng(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("This browser could not create the PNG logo."));
    }, "image/png");
  });
}

function rasterizedLogoName(name: string) {
  const stem = name.replace(/\.[^.]+$/, "").replace(/[^a-z0-9._-]+/gi, "-").replace(/^-+|-+$/g, "");
  return `${stem || "site-logo"}-rasterized.png`;
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function safeApiMessage(value: unknown, fallback: string) {
  if (!value || typeof value !== "object") return fallback;
  const candidate = value as { message?: unknown; error?: unknown };
  if (typeof candidate.message === "string" && candidate.message.length < 300) return candidate.message;
  if (typeof candidate.error === "string" && candidate.error.length < 300) return candidate.error;
  if (candidate.error && typeof candidate.error === "object") {
    const nested = candidate.error as { message?: unknown };
    if (typeof nested.message === "string" && nested.message.length < 300) return nested.message;
  }
  return fallback;
}
